import styled, { css } from 'styled-components';
import { mediaMaxWidth } from 'common/styles/breakpoints';

const BORDER_PROPERTIES = css`
    ${(props) => Object.entries(props.$borderWidth).map(([side, value]) => `border-${side}-width: ${value}px;`)};
    border-color: ${(props) => props.$borderColor};
    border-radius: ${(props) => props.$borderRadius}px;
    border-style: solid;
`;

const FONT_PROPERTIES = css`
    color: ${(props) => props.$fontColor};
    font-size: ${(props) => props.$fontSize}px;
    font-family: '${(props) => props.$fontFamily}', sans-serif;
`;

const SPEAKER_CIRCLE_CSS = css`
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    transition: 250ms ease transform;
    cursor: pointer;

    &:hover ~ * {
        transform: translateX(1rem);
    }
`;

export const Card = styled.div`
    padding: 2rem;
    border-style: solid;
    background-color: ${(props) => props.$backgroundColor};
    text-align: left;
    ${BORDER_PROPERTIES}

    ${mediaMaxWidth.medium`
        padding: 1.5rem;
    `}
`;

export const SessionCardHeader = styled.header`
    display: grid;
    grid-template-columns: 1fr 220px;
    ${mediaMaxWidth.medium`
        grid-template-columns: 1fr;
        gap: 0.5rem;
    `}
    justify-content: space-between;
    align-items: start;
`;

export const SessionText = styled.span`
    line-height: 1.4;
    cursor: default;
    word-break: break-word;
    white-space: pre-wrap;
    ${FONT_PROPERTIES}
`;

export const SessionLocation = styled.span`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    line-height: 1.4;
    cursor: default;
    word-break: break-word;
    ${FONT_PROPERTIES}
`;

export const SessionTitleText = styled(SessionText).attrs({ as: 'h3' })`
    flex: 5;
    margin: 0;
`;

export const SessionLabelContainer = styled.div`
    text-align: end;
    ${mediaMaxWidth.medium`
        text-align: start;
    `}
`;

export const SpeakerImagesContainer = styled.div`
    display: flex;

    & > :not(:first-child) {
        margin-left: -0.5rem;
    }
`;

export const SpeakerImage = styled.img`
    ${SPEAKER_CIRCLE_CSS}
    object-fit: cover;
    overflow: hidden;
`;

export const SpeakerPlaceholder = styled.div`
    ${SPEAKER_CIRCLE_CSS}
    text-transform: uppercase;
    font-family: '${(props) => props.$fontFamily}', sans-serif;
    font-size: 1rem;
    color: #1a1a1a;
    background-color: #dfdede; // eslint-disable-line splash/sui-colors-only
    border: 1px solid #535353; // eslint-disable-line splash/sui-colors-only
    display: inline-flex;
    justify-content: center;
    align-items: center;
`;

export const SpeakerDetailsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    gap: 0.75rem;

    ${mediaMaxWidth.medium`
        gap: 0.5rem;
    `}
`;

export const SpeakerDetails = styled.div`
    display: flex;
    gap: 1ch;
`;

const SINGLE_SPEAKERS_CSS = css`
    grid-template-columns: auto 1fr;

    ${SpeakerDetailsContainer} {
        flex-direction: row;
    }

    ${SpeakerDetails} {
        align-items: center;
        flex-wrap: wrap;
    }

    ${mediaMaxWidth.medium`
        grid-template-columns: 1fr;
    `}
`;

export const SpeakersContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.25rem;
    padding-bottom: 1rem;

    ${(props) => (!props.multiple ? SINGLE_SPEAKERS_CSS : '')};

    ${mediaMaxWidth.large`
        gap: 0.5rem;
    `}
`;

export const SessionsTagContainer = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
`;

export const SessionsFooterContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 2rem;

    &:focus {
        outline: none;
    }
`;

export const SessionsTag = styled.span`
    padding: 0.5rem 0.75rem;
    background-color: ${(props) => props.$backgroundColor};
    cursor: default;
    ${BORDER_PROPERTIES}
    ${FONT_PROPERTIES}
`;
